import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

const OVERVIEW_FIELDS = [
  "workingTitle",
  "screenFormat",
  "approximateIntendedDuration",
  "genre",
  "intendedAudience",
  "logline",
  "premiseOrShortSynopsis",
  "sourceOrAdaptationRelationship",
];

function clean(value) {
  return String(value ?? "").trim();
}

function normalizeOverview(value = {}) {
  const source =
    value &&
    typeof value === "object" &&
    !Array.isArray(value)
      ? value
      : {};

  return OVERVIEW_FIELDS.reduce(
    (result, field) => {
      result[field] =
        String(source[field] ?? "");

      return result;
    },
    {}
  );
}

function sameOverview(left, right) {
  return OVERVIEW_FIELDS.every(
    (field) =>
      String(left?.[field] ?? "") ===
      String(right?.[field] ?? "")
  );
}

export default function ScreenplayProjectOverview({
  project,
  projectPlan,
  loading = false,
  loadError = "",
  onReload,
  onSave,
  canEdit = true,
  isFR = false,
  writingModeLabel = "",
  updatedLabel = "",
}) {
  const persistedOverview =
    useMemo(
      () =>
        normalizeOverview(
          projectPlan?.overview
        ),
      [
        projectPlan?.overview,
      ]
    );

  const [form, setForm] =
    useState(persistedOverview);

  const [saving, setSaving] =
    useState(false);

  const [saveError, setSaveError] =
    useState("");

  const [saveMessage, setSaveMessage] =
    useState("");

  useEffect(() => {
    setForm(persistedOverview);
    setSaveError("");
    setSaveMessage("");
  }, [persistedOverview]);

  const revision =
    Number(projectPlan?.revision || 0);

  const dirty =
    !sameOverview(
      form,
      persistedOverview
    );

  function updateField(field, value) {
    if (!canEdit) {
      return;
    }

    setForm((current) => ({
      ...current,
      [field]: value,
    }));

    setSaveError("");
    setSaveMessage("");
  }

  async function submit(event) {
    event?.preventDefault?.();

    if (
      !canEdit ||
      saving ||
      !dirty ||
      revision < 1 ||
      typeof onSave !== "function"
    ) {
      return;
    }

    try {
      setSaving(true);
      setSaveError("");
      setSaveMessage("");

      await onSave(
        normalizeOverview(form),
        revision
      );

      setSaveMessage(
        isFR
          ? "Vue d’ensemble enregistrée."
          : "Project Overview saved."
      );
    } catch (error) {
      setSaveError(
        clean(error?.message) ||
        (
          isFR
            ? "Impossible d’enregistrer la vue d’ensemble."
            : "Could not save the Project Overview."
        )
      );
    } finally {
      setSaving(false);
    }
  }

  if (
    loading ||
    (!projectPlan && !loadError)
  ) {
    return (
      <section
        className="ns-card"
        style={{
          padding: 22,
          borderRadius: 20,
        }}
      >
        <strong>
          {isFR
            ? "Ouverture de la vue d’ensemble…"
            : "Opening Project Overview…"}
        </strong>
      </section>
    );
  }

  if (!projectPlan) {
    return (
      <section
        className="ns-card"
        style={{
          padding: 22,
          borderRadius: 20,
        }}
      >
        <p
          style={{
            marginTop: 0,
            color: "#b91c1c",
            fontWeight: 800,
          }}
        >
          {loadError ||
            (
              isFR
                ? "La vue d’ensemble du scénario n’a pas pu être ouverte."
                : "The Screenplay Project Overview could not be opened."
            )}
        </p>

        {typeof onReload ===
        "function" ? (
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={onReload}
          >
            {isFR
              ? "Réessayer"
              : "Try again"}
          </button>
        ) : null}
      </section>
    );
  }

  return (
    <form
      className="ns-card"
      onSubmit={submit}
      style={{
        padding: 22,
        borderRadius: 20,
        border:
          "1.5px solid rgba(220,38,38,0.24)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent:
            "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            maxWidth: 760,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#dc2626",
            }}
          >
            {isFR
              ? "VUE D’ENSEMBLE DU PROJET"
              : "PROJECT OVERVIEW"}
          </div>

          <h3
            className="ns-card-title"
            style={{
              margin: "6px 0 8px",
              fontSize: 26,
            }}
          >
            {clean(project?.title) ||
              (
                isFR
                  ? "Scénario sans titre"
                  : "Untitled Screenplay"
              )}
          </h3>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              lineHeight: 1.6,
              maxWidth: 720,
            }}
          >
            {isFR
              ? "Commencez avec ce que vous savez. Vous pourrez affiner chaque détail plus tard."
              : "Start with what you know. Every detail here can be refined later."}
          </p>
        </div>

        <div
          className="ns-card ns-card-soft"
          style={{
            padding: "12px 14px",
            borderRadius: 14,
            minWidth: 210,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 800,
              marginBottom: 5,
            }}
          >
            {isFR
              ? "Contexte du projet"
              : "Project context"}
          </div>

          <div
            className="ns-card-meta"
            style={{
              lineHeight: 1.55,
            }}
          >
            {clean(
              project?.primaryLanguage
            ) || "en"}
            {" · "}
            {writingModeLabel ||
              clean(
                project?.writingMode
              ) ||
              "Solo"}
            <br />
            {clean(
              project?.visibility
            ) || "private_draft"}
            {updatedLabel
              ? ` · ${updatedLabel}`
              : ""}
          </div>
        </div>
      </div>

      {!canEdit ? (
        <div
          className="ns-card ns-card-soft"
          role="status"
          style={{
            marginTop: 18,
            padding: 14,
            borderRadius: 14,
          }}
        >
          <strong>
            {isFR
              ? "Lecture seule"
              : "Read only"}
          </strong>
          <div
            className="ns-card-meta"
            style={{
              marginTop: 4,
            }}
          >
            {isFR
              ? "Seul le propriétaire peut modifier la vue d’ensemble du projet dans cette version."
              : "Only the project owner can edit the Project Overview in this version."}
          </div>
        </div>
      ) : null}

      <div
        className="ns-card ns-card-soft"
        style={{
          marginTop: 20,
          padding: 18,
          borderRadius: 18,
        }}
      >
        <h4
          className="ns-card-title"
          style={{
            margin: "0 0 6px",
            fontSize: 19,
          }}
        >
          {isFR
            ? "Donnez une forme à votre scénario"
            : "Shape your screenplay"}
        </h4>

        <p
          className="ns-card-meta"
          style={{
            margin: "0 0 16px",
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Ces choix nous aident à vous présenter les bons outils sans vous enfermer dans une formule."
            : "These details help NextScenes present the right tools without forcing your story into a formula."}
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(240px, 1fr))",
            gap: 14,
          }}
        >
          <label
            style={{
              display: "grid",
              gap: 7,
            }}
          >
            <span
              style={{
                fontWeight: 800,
              }}
            >
              {isFR
                ? "Titre de travail"
                : "Working title"}
            </span>

            <input
              className="ns-input"
              value={form.workingTitle}
              disabled={saving || !canEdit}
              onChange={(event) =>
                updateField(
                  "workingTitle",
                  event.target.value
                )
              }
              placeholder={
                isFR
                  ? "Le titre que vous utilisez pour l’instant"
                  : "What are you calling it for now?"
              }
            />
          </label>

          <label
            style={{
              display: "grid",
              gap: 7,
            }}
          >
            <span
              style={{
                fontWeight: 800,
              }}
            >
              {isFR
                ? "Format écran"
                : "Screen format"}
            </span>

            <input
              className="ns-input"
              list="screenplay-format-options"
              value={form.screenFormat}
              disabled={saving || !canEdit}
              onChange={(event) =>
                updateField(
                  "screenFormat",
                  event.target.value
                )
              }
              placeholder={
                isFR
                  ? "Long métrage, court métrage, épisode TV…"
                  : "Feature film, short film, TV episode…"
              }
            />

            <datalist id="screenplay-format-options">
              <option value="Feature Film" />
              <option value="Short Film" />
              <option value="TV Episode" />
              <option value="TV Pilot" />
              <option value="Limited Series Episode" />
              <option value="Web Series" />
              <option value="Documentary" />
              <option value="Animation" />
            </datalist>
          </label>

          <label
            style={{
              display: "grid",
              gap: 7,
            }}
          >
            <span
              style={{
                fontWeight: 800,
              }}
            >
              {isFR
                ? "Durée approximative"
                : "Approximate duration"}
            </span>

            <input
              className="ns-input"
              value={
                form.approximateIntendedDuration
              }
              disabled={saving || !canEdit}
              onChange={(event) =>
                updateField(
                  "approximateIntendedDuration",
                  event.target.value
                )
              }
              placeholder={
                isFR
                  ? "Ex. 90 minutes"
                  : "e.g. 90 minutes"
              }
            />
          </label>

          <label
            style={{
              display: "grid",
              gap: 7,
            }}
          >
            <span
              style={{
                fontWeight: 800,
              }}
            >
              {isFR
                ? "Genre"
                : "Genre"}
            </span>

            <input
              className="ns-input"
              value={form.genre}
              disabled={saving || !canEdit}
              onChange={(event) =>
                updateField(
                  "genre",
                  event.target.value
                )
              }
              placeholder={
                isFR
                  ? "Drame, comédie, thriller…"
                  : "Drama, comedy, thriller…"
              }
            />
          </label>

          <label
            style={{
              display: "grid",
              gap: 7,
            }}
          >
            <span
              style={{
                fontWeight: 800,
              }}
            >
              {isFR
                ? "Public visé"
                : "Intended audience"}
            </span>

            <input
              className="ns-input"
              value={
                form.intendedAudience
              }
              disabled={saving || !canEdit}
              onChange={(event) =>
                updateField(
                  "intendedAudience",
                  event.target.value
                )
              }
              placeholder={
                isFR
                  ? "Familles, adultes, jeunes adultes…"
                  : "Families, adults, young adults…"
              }
            />
          </label>
        </div>
      </div>

      <div
        className="ns-card ns-card-soft"
        style={{
          marginTop: 16,
          padding: 18,
          borderRadius: 18,
        }}
      >
        <h4
          className="ns-card-title"
          style={{
            margin: "0 0 6px",
            fontSize: 19,
          }}
        >
          {isFR
            ? "Le cœur de l’histoire"
            : "The heart of the story"}
        </h4>

        <p
          className="ns-card-meta"
          style={{
            margin: "0 0 16px",
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Ne cherchez pas encore la perfection. Capturez simplement l’idée que vous voulez porter à l’écran."
            : "Do not chase perfection yet. Capture the idea you want to put on screen."}
        </p>

        <label
          style={{
            display: "grid",
            gap: 7,
          }}
        >
          <span
            style={{
              fontWeight: 800,
            }}
          >
            {isFR
              ? "Logline"
              : "Logline"}
          </span>

          <textarea
            className="ns-input"
            rows={3}
            value={form.logline}
            disabled={saving || !canEdit}
            onChange={(event) =>
              updateField(
                "logline",
                event.target.value
              )
            }
            placeholder={
              isFR
                ? "Une ou deux phrases qui capturent le personnage principal, son objectif et le conflit central."
                : "One or two sentences capturing the central character, goal, and dramatic conflict."
            }
            style={{
              resize: "vertical",
              minHeight: 92,
            }}
          />
        </label>

        <label
          style={{
            display: "grid",
            gap: 7,
            marginTop: 14,
          }}
        >
          <span
            style={{
              fontWeight: 800,
            }}
          >
            {isFR
              ? "Prémisse ou court synopsis"
              : "Premise / short synopsis"}
          </span>

          <textarea
            className="ns-input"
            rows={7}
            value={
              form.premiseOrShortSynopsis
            }
            disabled={saving || !canEdit}
            onChange={(event) =>
              updateField(
                "premiseOrShortSynopsis",
                event.target.value
              )
            }
            placeholder={
              isFR
                ? "Décrivez brièvement ce qui arrive, à qui, et pourquoi cela compte."
                : "Briefly describe what happens, to whom, and why it matters."
            }
            style={{
              resize: "vertical",
              minHeight: 160,
            }}
          />
        </label>
      </div>

      <div
        className="ns-card ns-card-soft"
        style={{
          marginTop: 16,
          padding: 18,
          borderRadius: 18,
        }}
      >
        <h4
          className="ns-card-title"
          style={{
            margin: "0 0 6px",
            fontSize: 19,
          }}
        >
          {isFR
            ? "Source et adaptation"
            : "Source & adaptation"}
        </h4>

        <p
          className="ns-card-meta"
          style={{
            margin: "0 0 14px",
            lineHeight: 1.5,
          }}
        >
          {isFR
            ? "Laissez vide s’il s’agit d’une œuvre originale sans relation particulière avec une œuvre existante."
            : "Leave this blank if the screenplay is wholly original and has no special relationship to an existing work."}
        </p>

        <textarea
          className="ns-input"
          rows={4}
          value={
            form.sourceOrAdaptationRelationship
          }
          disabled={saving || !canEdit}
          onChange={(event) =>
            updateField(
              "sourceOrAdaptationRelationship",
              event.target.value
            )
          }
          placeholder={
            isFR
              ? "Ex. Adapté de mon roman… Inspiré d’une histoire vraie…"
              : "e.g. Adapted from my novel… Inspired by a true story…"
          }
          style={{
            width: "100%",
            resize: "vertical",
            minHeight: 110,
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 18,
        }}
      >
        <button
          className="ns-button ns-button-primary"
          type="submit"
          disabled={
            !canEdit ||
            saving ||
            !dirty ||
            revision < 1
          }
        >
          {saving
            ? (
                isFR
                  ? "Enregistrement…"
                  : "Saving…"
              )
            : (
                isFR
                  ? "Enregistrer la vue d’ensemble"
                  : "Save Project Overview"
              )}
        </button>

        {!dirty &&
        !saveMessage &&
        !saveError ? (
          <span
            className="ns-card-meta"
          >
            {isFR
              ? "Aucune modification non enregistrée."
              : "No unsaved changes."}
          </span>
        ) : null}

        {saveMessage ? (
          <span
            style={{
              fontWeight: 800,
              color: "#166534",
            }}
          >
            {saveMessage}
          </span>
        ) : null}

        {saveError ? (
          <span
            style={{
              fontWeight: 800,
              color: "#b91c1c",
            }}
          >
            {saveError}
          </span>
        ) : null}
      </div>
    </form>
  );
}
